// This Lite Component allows for simple Rich Text with acumin fonts and allows to be embededd in other Rich Text Renderers and prevents loops
/* eslint-disable react/destructuring-assignment */
import type { PropsWithChildren } from "react"
import React from "react"
import type { Document, Node } from "@contentful/rich-text-types"
import { BLOCKS, INLINES } from "@contentful/rich-text-types"
import { Text, UnorderedList, OrderedList } from "@chakra-ui/react"
import { Link } from "components/Link"
import { documentToReactComponents } from "@contentful/rich-text-react-renderer"
import { checkLinkTarget } from "components/Blog/utils/Blog.utils"

type Props = {
  content: Document
  fontFam?: string
  paragraphNode?: React.FC<PropsWithChildren>
  linkItemNode?: React.FC<PropsWithChildren & { href: string }>
}

export const RichTextRendererLite: React.FC<Props> = ({
  content,
  fontFam,
  paragraphNode: ParagraphNode,
  linkItemNode: LinkItemNode,
}) => {
  const options = {
    renderNode: {
      // Text Elements
      [BLOCKS.PARAGRAPH]: (_node: Node, children: React.ReactNode) =>
        ParagraphNode ? (
          <ParagraphNode>{children}</ParagraphNode>
        ) : (
          <Text variant={fontFam || "acuminLight"} mt="1.5rem">
            {children}
          </Text>
        ),
      [BLOCKS.UL_LIST]: (_node: Node, children: React.ReactNode) => (
        <UnorderedList mt="1rem">{children}</UnorderedList>
      ),
      [BLOCKS.OL_LIST]: (_node: Node, children: React.ReactNode) => (
        <OrderedList mt="1rem">{children}</OrderedList>
      ),
      // Links
      [INLINES.HYPERLINK]: (node: Node, children: React.ReactNode) =>
        LinkItemNode ? (
          <LinkItemNode href={node.data?.uri || ""}>{children}</LinkItemNode>
        ) : (
          <Link
            as="a"
            href={node.data?.uri || ""}
            title={children as string}
            variant="acuminRegular"
            target={checkLinkTarget(node.data.uri)}
            textDecor="underline"
            _hover={{
              textDecor: "underline !important",
            }}
          >
            {children}
          </Link>
        ),
    },
    // This callback function allows the shift return function to work in the rich text editor
    renderText: (text) => {
      return text.split("\n").reduce((children, textSegment, idx) => {
        const key = `br-${idx}`
        return [...children, idx > 0 && <br key={key} />, textSegment]
      }, [])
    },
  }
  return <>{documentToReactComponents(content, options)}</>
}
